import { UseMutationOptions, useQueryClient, useMutation } from "@tanstack/react-query";
import { track } from "./analytics";
import { CsError } from "./CsError";
const origin = process.env.REACT_APP_API_ORIGIN ?? ''
type ApiQueryKey = readonly [string]

export function useUnauthedApiMutation<TResponse, TRequestBody = undefined>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  mutationOptions?: Omit<UseMutationOptions<TResponse, CsError, TRequestBody>, 'mutationFn'>
) {
  const mutationKey = [path] as ApiQueryKey
  const queryClient = useQueryClient()

  return useMutation<TResponse, CsError, TRequestBody>({
    onSuccess: (data: any) => {
      // Cache the result so it can be reused
      queryClient.setQueryData(mutationKey, data);
    },
    ...mutationOptions,
    mutationKey,
    mutationFn: async (body: TRequestBody) => {
      const url = `${origin}/api/${path}`;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (res.ok) {
        try {
          return (await res.json()) as TResponse;
        } catch (e) {
          track('error', 'useUnauthedApiMutation', {}, {
            message: 'Empty success response, returning empty object',
            error: JSON.stringify(e),
          });
          return {} as TResponse;
        }
      } else {
        let errorData: any = null;
        try {
          errorData = await res.json();
        } catch (e) {
          track('error', 'useUnauthedApiMutation', {}, {
            message: 'Failed to parse error response as JSON',
            error: JSON.stringify(e),
          });
          errorData = { userErrorMessage: 'An error occurred' };
        }
        const err: CsError = {...errorData, status: res?.status }
        throw err 
      }
    },
  });
}
